import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

import {
  FiLock,
  FiEye,
  FiEyeOff,
  FiLogOut,
  FiUser,
  FiMail,
  FiPhone,
  FiSend,
  FiX,
  FiPaperclip,
  FiGift,
  FiClock,
} from "react-icons/fi";

import { FaWhatsapp } from "react-icons/fa";

import fondo from "../assets/fondo1.jpg";
import Tooltip from "./Tooltip";
import FloatingAdminMenu from "./FloatMenu";

const API = "https://www.aldanaselena.com/mentorias.php";
const API_PREMIOS = "https://www.aldanaselena.com/premios.php";
const API_EMAIL = "https://aldanaselena.com/responder.php";
const PASSWORD = "aldana.admin";

// Colores del badge de estado de cada premio de la ruleta
const ESTADO_CLASES = {
  vigente: "text-green-400 bg-green-500/10 border-green-500/30",
  usado: "text-[#B8977F] bg-[#B8977F]/10 border-[#B8977F]/40",
  vencido: "text-red-300 bg-red-500/10 border-red-500/30",
};

const NAVBAR_HEIGHT = 64;

const AdminMentorias = () => {
  const [data, setData] = useState([]);

  // PREMIOS RULETA
  const [premios, setPremios] = useState([]);
  const [loadingPremios, setLoadingPremios] = useState(false);
  const [vista, setVista] = useState("consultas");

  const [isAuth, setIsAuth] = useState(false);

  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  // MENU
  const [showMenu, setShowMenu] = useState(false);

  // EMAIL MODAL
  const [selectedUser, setSelectedUser] = useState(null);

  const [mensaje, setMensaje] = useState("");

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  // ================= AUTH =================
  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") {
      setIsAuth(true);
    }
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true);
      localStorage.setItem("admin_auth", "true");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuth(false);
  };

  // ================= FETCH =================
  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(API);

      setData(res.data);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const fetchPremios = async () => {
    try {
      setLoadingPremios(true);

      const res = await axios.get(API_PREMIOS);

      setPremios(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }

    setLoadingPremios(false);
  };

  useEffect(() => {
    if (isAuth) {
      fetchData();
      fetchPremios();
    }
  }, [isAuth]);

  // ================= WHATSAPP =================
  const goToWhatsApp = (telefono, nombre) => {
    const clean = telefono.replace(/\D/g, "");

    const url = `https://wa.me/${clean}?text=Hola ${nombre}, te contacto por tu solicitud de mentoría ✨`;

    window.open(url, "_blank");
  };

  // ================= IMAGE COMPRESS =================
  const compressImage = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => (img.src = e.target.result);

      img.onload = () => {
        const canvas = document.createElement("canvas");

        const scale = Math.min(600 / img.width, 1);

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };

      reader.readAsDataURL(file);
    });

  const handleFile = async (e) => {
    const selected = e.target.files[0];

    if (!selected) return;

    const compressed = await compressImage(selected);

    setFile(compressed);
    setFileName(selected.name);
  };

  // ================= SEND EMAIL =================
  const sendEmail = async () => {
    if (!mensaje) return;

    try {
      setSending(true);

      await axios.post(API_EMAIL, {
        email: selectedUser.email,
        nombre: selectedUser.nombre,
        mensaje,
        imagen: file,
      });

      setSuccess(true);

      setTimeout(() => {
        setSelectedUser(null);
        setMensaje("");
        setFile(null);
        setFileName("");
        setSuccess(false);
      }, 1500);
    } catch (err) {
      console.error(err);
    }

    setSending(false);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* BG */}
      <img
        src={fondo}
        alt="bg"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="absolute inset-0 bg-[#0E0B09]/90 backdrop-blur-sm" />

      {/* LOGIN */}
      <AnimatePresence>
        {!isAuth && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[99999] px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              className="
              relative z-10
              bg-[#1a1411]
              p-7
              rounded-3xl
              w-full max-w-sm
              border border-[#B8977F]/30
              shadow-2xl
              "
            >
              <div className="flex items-center gap-3 text-[#B8977F] mb-6">
                <div className="p-2 rounded-xl bg-[#B8977F]/10">
                  <FiLock size={18} />
                </div>

                <h2 className="tracking-[0.2em] text-lg">
                  ACCESO ADMIN
                </h2>
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Contraseña"
                  className="
                  w-full
                  p-4
                  rounded-2xl
                  bg-black/40
                  text-white
                  border border-[#B8977F]/20
                  outline-none
                  focus:border-[#B8977F]/50
                  transition
                  "
                />

                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="
                  absolute right-4 top-1/2 -translate-y-1/2
                  text-[#B8977F]
                  "
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <button
                onClick={handleLogin}
                className="
                mt-5
                w-full
                bg-[#B8977F]
                text-black
                py-3
                rounded-2xl
                hover:opacity-80
                transition
                font-medium
                "
              >
                Ingresar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MENU */}
      {isAuth && (
        <FloatingAdminMenu
          showMenu={showMenu}
          setShowMenu={setShowMenu}
        />
      )}

      {/* PANEL */}
      {isAuth && (
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-12 pt-28 pb-16">
          {/* HEADER */}
          <div className="flex justify-between items-center mb-10">
            <div>
              <h1
                className="
                text-3xl md:text-5xl
                text-[#B8977F]
                tracking-[0.25em]
                "
              >
               PANEL DE MENTORÍAS
              </h1>

              <div className="w-24 h-px bg-[#B8977F]/30 mt-4" />
            </div>

            <button
              onClick={handleLogout}
              className="
              flex items-center gap-2
              border border-[#B8977F]/30
              px-4 py-3
              rounded-2xl
              text-[#B8977F]
              hover:bg-[#B8977F]/10
              transition
              "
            >
              <FiLogOut />
              Salir
            </button>
          </div>

          {/* TABS */}
          <div className="flex flex-wrap gap-3 mb-10">
            <button
              onClick={() => setVista("consultas")}
              className={`
                px-5 py-3 rounded-2xl text-xs tracking-[0.2em] transition
                ${
                  vista === "consultas"
                    ? "bg-[#B8977F] text-black"
                    : "border border-[#B8977F]/30 text-[#B8977F] hover:bg-[#B8977F]/10"
                }
              `}
            >
              CONSULTAS ({data.length})
            </button>

            <button
              onClick={() => {
                setVista("premios");
                fetchPremios();
              }}
              className={`
                flex items-center gap-2
                px-5 py-3 rounded-2xl text-xs tracking-[0.2em] transition
                ${
                  vista === "premios"
                    ? "bg-[#B8977F] text-black"
                    : "border border-[#B8977F]/30 text-[#B8977F] hover:bg-[#B8977F]/10"
                }
              `}
            >
              <FiGift />
              PREMIOS RULETA ({premios.length})
            </button>
          </div>

          {/* CONTENT */}
          {vista === "premios" ? (
            loadingPremios ? (
              <div className="flex justify-center py-20">
                <p className="text-[#B8977F] text-lg">
                  Cargando premios...
                </p>
              </div>
            ) : premios.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <p className="text-[#B8977F] text-lg md:text-xl tracking-[0.2em]">
                  TODAVÍA NADIE GIRÓ LA RULETA
                </p>

                <div className="w-20 h-px bg-[#B8977F]/30 mt-5" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {premios.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="
                    bg-[#1a1411]/80
                    p-6
                    rounded-3xl
                    border border-[#3C2B22]
                    shadow-xl
                    backdrop-blur-xl
                    "
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-white flex items-center gap-3 tracking-[0.15em]">
                        <FiGift className="text-[#B8977F] flex-shrink-0" />
                        {p.codigo}
                      </p>

                      <span
                        className={`
                          px-3 py-1 rounded-full border
                          text-[10px] tracking-[0.2em] uppercase
                          ${ESTADO_CLASES[p.estado] || ESTADO_CLASES.vigente}
                        `}
                      >
                        {p.estado}
                      </span>
                    </div>

                    <p className="text-[#D4BCAA] mt-4 text-sm tracking-wider">
                      {p.premio}
                    </p>

                    <div className="mt-5 space-y-1.5 text-xs text-[#7A5E50]">
                      <p className="flex items-center gap-2">
                        <FiClock className="flex-shrink-0" />
                        Girado: {p.creado}
                      </p>
                      <p className="pl-6">Vence: {p.expira || "—"}</p>
                      {p.usado_en && (
                        <p className="pl-6 text-[#B8977F]">
                          Usado: {p.usado_en}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )
          ) : loading ? (
            <div className="flex justify-center py-20">
              <p className="text-[#B8977F] text-lg">
                Cargando mentorías...
              </p>
            </div>
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p
                className="
                text-[#B8977F]
                text-lg md:text-xl
                tracking-[0.2em]
                "
              >
                NO HAY SOLICITUDES
              </p>

              <div className="w-20 h-px bg-[#B8977F]/30 mt-5" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              {data.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="
                  bg-[#1a1411]/80
                  p-6
                  rounded-3xl
                  border border-[#3C2B22]
                  shadow-xl
                  backdrop-blur-xl
                  "
                >
                  <div className="space-y-2">
                    <p className="text-white flex items-center gap-3 text-lg">
                      <FiUser className="text-[#B8977F]" />
                      {item.nombre} {item.apellido}
                    </p>

                    <p className="text-[#B8977F] flex items-center gap-3 text-sm">
                      <FiMail />
                      {item.email}
                    </p>

                    <p className="text-[#B8977F] flex items-center gap-3 text-sm">
                      <FiPhone />
                      {item.telefono}
                    </p>
                  </div>

                  <p className="text-[#D4BCAA] text-sm mt-5 italic leading-relaxed">
                    {item.objetivo}
                  </p>

                  {/* CÓDIGO DE DESCUENTO DE LA RULETA */}
                  {item.codigo_descuento && (
                    <div
                      className="
                      mt-4
                      flex items-center gap-2
                      border border-[#B8977F]/40
                      bg-[#B8977F]/10
                      rounded-2xl
                      px-4 py-2.5
                      "
                    >
                      <FiGift className="text-[#B8977F] flex-shrink-0" />
                      <span className="text-[#D4BCAA] text-xs tracking-[0.15em] uppercase">
                        {item.premio || "Descuento"} · {item.codigo_descuento}
                      </span>
                    </div>
                  )}

                  {/* ACTIONS */}
                  <div className="flex justify-center gap-6 mt-8">
                    <Tooltip text="Contactar por WhatsApp">
                      <button
                        onClick={() =>
                          goToWhatsApp(item.telefono, item.nombre)
                        }
                        className="
                        w-12 h-12
                        flex items-center justify-center
                        rounded-2xl
                        bg-green-500/10
                        text-green-400
                        hover:scale-110
                        transition
                        "
                      >
                        <FaWhatsapp size={20} />
                      </button>
                    </Tooltip>

                    <Tooltip text="Responder por email">
                      <button
                        onClick={() => setSelectedUser(item)}
                        className="
                        w-12 h-12
                        flex items-center justify-center
                        rounded-2xl
                        bg-[#B8977F]
                        text-black
                        hover:brightness-110
                        transition
                        "
                      >
                        <FiSend size={18} />
                      </button>
                    </Tooltip>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* EMAIL MODAL */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            className="fixed inset-0 z-[99998] flex justify-center items-center px-3 sm:px-4"
            style={{
              paddingTop: NAVBAR_HEIGHT,
              paddingBottom: "env(safe-area-inset-bottom, 16px)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* overlay */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

            {/* modal */}
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              className="
              relative
              w-full max-w-md md:max-w-lg
              max-h-[85vh]
              rounded-3xl
              bg-[#0E0B09]
              border border-[#3C2B22]
              flex flex-col
              overflow-hidden
              "
            >
              {/* HEADER */}
              <div className="flex justify-between items-center px-5 py-5 border-b border-[#3C2B22]">
                <div className="flex flex-col">
                  <span className="text-[#B8977F] text-sm flex items-center gap-2">
                    <FiMail />
                    Responder mentoría
                  </span>

                  <span className="text-xs text-[#EDE0D0]/80 mt-1">
                    {selectedUser.nombre} · {selectedUser.email}
                  </span>
                </div>

                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-[#B8977F]"
                >
                  <FiX />
                </button>
              </div>

              {/* BODY */}
              <div className="p-5 space-y-5 flex-1 overflow-y-auto">
                {success ? (
                  <div className="flex justify-center items-center py-14">
                    <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center text-white text-2xl">
                      ✔
                    </div>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder="Escribí tu mensaje..."
                      className="
                      w-full
                      min-h-[140px]
                      bg-[#1C1410]
                      text-white
                      p-4
                      rounded-2xl
                      border border-[#3C2B22]
                      outline-none
                      resize-none
                      "
                    />

                    <Tooltip text="Adjuntar imagen">
                      <label className="flex items-center gap-2 text-[#7A5E50] cursor-pointer">
                        <FiPaperclip />

                        <span className="text-sm">
                          Adjuntar imagen
                        </span>

                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFile}
                        />
                      </label>
                    </Tooltip>

                    {file && (
                      <div className="flex items-center gap-4">
                        <img
                          src={file}
                          alt="preview"
                          className="w-16 h-16 object-cover rounded-xl"
                        />

                        <span className="text-xs text-[#B8977F]">
                          {fileName}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* FOOTER */}
              {!success && (
                <div className="flex justify-between px-5 py-4 border-t border-[#3C2B22]">
                  <Tooltip text="Enviar email">
                    <button
                      onClick={sendEmail}
                      disabled={sending}
                      className="
                      bg-[#B8977F]
                      w-12 h-12
                      rounded-2xl
                      flex items-center justify-center
                      text-black
                      hover:brightness-110
                      transition
                      disabled:opacity-50
                      "
                    >
                      <FiSend />
                    </button>
                  </Tooltip>

                  <Tooltip text="Cerrar">
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="
                      w-12 h-12
                      border border-[#7A5E50]
                      rounded-2xl
                      flex items-center justify-center
                      text-[#7A5E50]
                      hover:bg-[#7A5E50]/10
                      transition
                      "
                    >
                      <FiX />
                    </button>
                  </Tooltip>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminMentorias;